import { disableSubmitButton } from 'formifier';
import _ from 'lodash';
import LockWrapper from './lockWrapper/lockWrapper';
import { WrappedElement } from './WrappedElement';
import ow from 'ow';
import { notBlankValidator } from './owValidators';

export const AUTOFOCUS_CALLBACK = {
  email: 'email',
  password: 'password',
  default: 'default',
};

export class Renderer {
  constructor(
    lockWrapper,
    backToSignInViewState,
    wrappedElements,
    autofocuser,
    auth0Domain,
    handlingErrors
  ) {
    if (!(lockWrapper instanceof LockWrapper)) {
      throw new Error('Expected lockWrapper to be a LockWrapper');
    }
    if (!_.isFunction(backToSignInViewState)) {
      throw new Error('Expected backToSignInViewState to be a function');
    }
    if (!this.isValidWrappedElements(wrappedElements)) {
      throw new Error(
        'Expected wrappedElements to be a collection of WrappedElements'
      );
    }
    ow(auth0Domain, 'auth0Domain', ow.string.validate(notBlankValidator));

    this.lockWrapper = lockWrapper;
    this.backToSignInViewState = backToSignInViewState;
    this.wrappedElements = wrappedElements;
    this.autofocuser = autofocuser;
    this.auth0Domain = auth0Domain;
    this.handlingErrors = handlingErrors;
  }

  isValidWrappedElements(elements) {
    if (!(_.isPlainObject(elements) && _.size(elements) > 0)) {
      return false;
    }

    return _.every(elements, (el) => el instanceof WrappedElement);
  }

  toggleVisibility(els, view) {
    _.forEach(els, (value) => {
      value.toggleVisibility(view);
    });
  }

  updateText(els, view) {
    _.forEach(els, (value) => {
      value.updateText(view);
    });
  }

  redirectToConnection(newConnection, auth0Domain) {
    let params = new URLSearchParams(window.location.search);
    params.set('connection', newConnection);
    if (params.has('client')) {
      params.set('client_id', params.get('client'));
      params.delete('client');
    }
    window.location = `https://${auth0Domain}/authorize?${params}`;
  }

  disableLockEmailFields(wrappedElements) {
    _.forEach(wrappedElements, (value) => {
      value.disable();
    });
  }

  isEmailScreen(viewState) {
    return viewState.isEmailFormVisible;
  }

  isPasswordScreen(viewState) {
    if (viewState.lockState?.lockOptionOverrides?.initialScreen) {
      return ['login', 'signUp'].includes(
        viewState.lockState.lockOptionOverrides.initialScreen
      );
    }
    return false;
  }

  render(viewState) {
    let autofocusType;

    if (viewState.federatedConnection) {
      this.redirectToConnection(
        viewState.federatedConnection,
        this.auth0Domain
      );
      return;
    }

    if (viewState.isEmailFormVisible) {
      disableSubmitButton(this.wrappedElements.emailForm.element, false);
    }

    if (this.isEmailScreen(viewState)) {
      autofocusType = AUTOFOCUS_CALLBACK.email;
    } else if (this.isPasswordScreen(viewState)) {
      autofocusType = AUTOFOCUS_CALLBACK.password;
    } else {
      autofocusType = AUTOFOCUS_CALLBACK.default;
    }

    this.updateText(this.wrappedElements, viewState);
    this.toggleVisibility(this.wrappedElements, viewState);

    this.lockWrapper.render(
      viewState.lockState,
      this.handlingErrors(() => this.autofocuser.start(autofocusType)),
      this.handlingErrors((screenName) => {
        this.disableLockEmailFields(this.wrappedElements);
        this.wrappedElements.auth0WrapperDiv.setAttribute(
          'data-analytics-form',
          `auth0_lock_${screenName}`
        );
        document.dispatchEvent(new CustomEvent('formUpdate'));
      }),
      this.handlingErrors(() =>
        this.backToSignInViewState().then((vs) => this.render(vs))
      )
    );
  }
}
